import { EmbeddableMetricDataQualityWarning } from '@tableau/data-catalog-client-components';
import { LabelAssetType } from '@tableau/data-catalog-shared';
import { ConfigurationText, EffectiveDefinition, isBeingWarehoused } from '@tableau/metrics-engine';
import { MetricSnapshotBadge } from '@tableau/metrics-shared-widgets-dom';
import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { DeprecatedInterpolatedString } from '~commonInfra/components/InterpolatedString';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { useSelector } from '~src/settings/general-settings/utils';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { LocationField } from '~src/spacesPlatform/components/tooltip/LocationField';
import { Name } from '~src/spacesPlatform/components/tooltip/Name';
import { Timestamp } from '~src/spacesPlatform/components/tooltip/Timestamp';
import { Tooltip } from '~src/spacesPlatform/components/tooltip/Tooltip';
import { ContentTooltipProps, getPersonalSpaceLink, getProjectLink, TooltipDescriptionTestId } from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { Tags } from '~src/tags/components/tooltips/Tags';

import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { HelpService } from '../../commonInfra/services/HelpService';
import { IUserInfo } from '../../userManagement/users/models/IUserInfo';
import { resumeMetricFromExplorer } from '../actions/UpdateMetricsActions';
import { IMetricInfo, isUsageMetricInfo } from '../models/IMetricInfo';
import * as MetricsSelectors from '../selectors';
import { MetricConfigurationInfo } from './tooltips/MetricConfigurationInfo';

interface MetricTooltipStateProps {
  userCanResumeSuspendedMetric: boolean;
  catalogingOnSiteEnabled: boolean;
  isOwner: boolean;
}

interface MetricTooltipDispatchProps {
  resumeMetricFromExplorer: (metricId: string, isOwner: boolean) => void;
}

export type MetricTooltipProps =
  MetricTooltipStateProps &
  MetricTooltipDispatchProps &
  ContentTooltipProps &
  { definition?: EffectiveDefinition, configurationText?: ConfigurationText };

export function MetricTooltip(props: MetricTooltipProps): JSX.Element {
  const { urls } = props;
  const content = props.content as IMetricInfo;

  const owner: IUserInfo = content.owner;

  // workaround for the incompleteness of the original metric content type.
  const description = content.description;
  const tags = content.tags;

  /* Just commenting this out in case we wanted to quickly add connectedView's back in.
  const connectedView = content.connectedView;

  let connectedViewLink: EntityLinkProps;
  if (connectedView) {
    const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;
    connectedViewLink = {
      href: urls.viewUrl(connectedView.path),
      iconImagePath: null,
      name: viewName
    };
  } else {
    connectedViewLink = {
      name: Messages.unavailable(),
      textStyleOverride: GreyedOutTextStyle,
      href: '',
      iconImagePath: null
    };
  }
  */

  const projectLink = getProjectLink(content.project, urls);
  const personalSpaceLink = getPersonalSpaceLink(urls);
  const locationType = content.location?.type;
  const ownerId = content.owner?.id;

  const itemInfo = content as unknown as IContentInfo<Extract<ExplorableItemType, LabelAssetType>>;

  return (
    <Tooltip>
      <Name name={content.name} />
      {isBeingWarehoused(props.definition?.features) && (
        <div style={badgeContainerStyle}>
          <MetricSnapshotBadge />
        </div>
      )}
      {content.suspended && <MetricSuspendedWarning {...props} />}
      {FeatureFlags.labelsInMorePlacesEnabled?
        <LabelWrapper itemInfo={itemInfo} category={'warning'} kind={'chip'} masthead={true}/>
      : <RenderDataQualityWarnings {...props}/> }
      <MarkupField markup={description}
        testId={TooltipDescriptionTestId}
        extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={tags} />
      {/*<RelatedEntity label={Messages.metricConnectedView()} link={connectedViewLink}/>*/}
      <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace}
        locationType={locationType} projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId} />
      <User user={owner} label={Messages.owner()} urls={urls} />
      { !isUsageMetricInfo(content) && <CompactInlineAlertWidget
        type={InlineAlertType.Info}
        testId='metric-authentication-info-alert'
        style={{ marginBottom: '18px' }}
        children={
          <>{Messages.metricsAuthenticationInfo()}
            {' '}
            <TextLinkWidget href={HelpService.getServerHelpUrlByCategory('metrics-data')} testId="link">{Messages.metricsAuthenticationInfoLinkText()}</TextLinkWidget>
          </>
        }
        />
      }
      {
        FeatureFlags.metricsHistoricalComparisonsEnabled && FeatureFlags.metricsDateWindowEnabled && props.definition &&
        <MetricConfigurationInfo
          configurationText={props.configurationText}
        />}
      {/* "Refreshed": Last time the metric data store gets updated
          "Modified": last time the metric itself was modified */}
      {props.lastUpdatedTimestamp && <Timestamp label={Messages.metricLastUpdated()} value={props.lastUpdatedTimestamp} />}
    </Tooltip>
  );
}

/** @deprecated TFS1478812 */
function RenderDataQualityWarnings(props: MetricTooltipProps): JSX.Element {
  const content = props.content as IMetricInfo;
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  if (props.catalogingOnSiteEnabled) {
    return (
      <div style={{ marginBottom: '18px' }}>
        <EmbeddableMetricDataQualityWarning
          apiRequests={CatalogApiRequests}
          uiActions={DataCatalogUIActionsService.instance()}
          siteUrls={props.urls} featureFlags={catalogFeatureFlagsAdapter()}
          client={catalogApolloClient} metricLuid={content.luid} side={'left'}
          telemetryContext={getTelemetryCommonProperties()}
          source={'MetricTooltip'}
          siteLuid={siteLuid}
        />
      </div>
    );
  }
}

function MetricSuspendedWarning(props: MetricTooltipProps): JSX.Element {
  const inlineAlertProps = {
    testId: 'metrics-suspended',
    type: InlineAlertType.Warning,
    style: { marginBottom: '18px' }
  };

  if (props.userCanResumeSuspendedMetric) {
    const resumeButton = (
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId="metrics-resume"
        onClick={() => props.resumeMetricFromExplorer(props.content.id, props.isOwner)}
      >
        {Messages.metricResumeButtonLabel()}
      </ButtonWidget>
    );

    return (
      <InlineAlertWidget {...inlineAlertProps} button={resumeButton}>
        {renderSuspendedWarningWithResumeContent(props)}
      </InlineAlertWidget>
    );

  } else {
    return (
      <InlineAlertWidget {...inlineAlertProps}>
        {Messages.metricSuspendedNoResumeTooltip()}
      </InlineAlertWidget>
    );
  }
}

function renderSuspendedWarningWithResumeContent(props: MetricTooltipProps): JSX.Element {
  const learnMoreLink = (
    <TextLinkWidget
      href={HelpService.getServerHelpUrlByCategory('metrics-suspend')}
      target="_blank"
      testId="metrics-resume-help"
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  return (
    <DeprecatedInterpolatedString
      baseStringKey="metricSuspendedWithResumeTooltip"
      translate={() => Messages.metricSuspendedWithResumeTooltip({ LEARN_MORE_LINK: '{LEARN_MORE_LINK}' })}
      replacements={{
        'SUSPEND_DATE': () => <span>{L10n.localizedDate(props.lastUpdatedTimestamp)}</span>,
        'LEARN_MORE_LINK': () => learnMoreLink,
      }}
    />
  );
}

const badgeContainerStyle: React.CSSProperties = {
  marginBottom: '8px',
  marginTop: '-8px',
};

const mapStateToProps: MapStateToProps<MetricTooltipStateProps, ContentTooltipProps> = (state, ownProps) => ({
  userCanResumeSuspendedMetric: MetricsSelectors.userCanResumeSuspendedMetric(state, ownProps.content),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state),
  isOwner: MetricsSelectors.userIsMetricOwner(state, ownProps.content),
});

const mapDispatchToProps: MapDispatchToProps<MetricTooltipDispatchProps> = (dispatch) => bindActionCreators({
  resumeMetricFromExplorer
}, dispatch);

export const ConnectedMetricTooltip = connect(mapStateToProps, mapDispatchToProps)(MetricTooltip);
